import type { NavDataType } from "@/constants/common";
import {
  ECARE_APP_GROUP_LABELS,
  NAV_BAR_HEIGHT_PX,
  NAV_BAR_LABELS,
} from "./nav-bar-constant";
import type { EcareApp, EcareAppGroup, EcareAppSection } from "./nav-bar-types";

/** Drops the query string and any trailing slash so two paths compare cleanly. */
const normalisePath = (path: string) =>
  path.split("?")[0].replace(/\/+$/, "") || "/";

const isPathPrefix = (candidate: string, pathname: string) => {
  const base = normalisePath(candidate);
  const current = normalisePath(pathname);
  return current === base || current.startsWith(`${base}/`);
};

/**
 * Resolves the highlighted top-level tab by longest matching route prefix, so a
 * tab stays lit on nested routes its children do not list.
 */
export const resolveActiveNavValue = (
  menu: NavDataType[],
  pathname: string,
  fallbackValue: string
): string => {
  let bestValue = fallbackValue;
  let bestLength = -1;

  const consider = (path: string | undefined, value: string) => {
    if (!path || !isPathPrefix(path, pathname)) return;
    const length = normalisePath(path).length;
    if (length > bestLength) {
      bestLength = length;
      bestValue = value;
    }
  };

  menu.forEach((item) => {
    consider(item.path, item.value);
    item.children?.forEach((child) => consider(child.path, item.value));
  });

  return bestValue;
};

/** "Dr. Vora" for providers, "Jane Cooper" for everyone else. */
export const getNavBarShortName = (
  firstName: string,
  lastName: string,
  isProvider: boolean
): string => {
  if (isProvider && lastName)
    return `${NAV_BAR_LABELS.PROVIDER_NAME_PREFIX} ${lastName}`;
  return [firstName, lastName].filter(Boolean).join(" ");
};

/** Viewport height left under the bar, minus `chromePx` of surrounding card chrome. */
export const heightBelowNavBar = (chromePx: number) =>
  `calc(100vh - ${NAV_BAR_HEIGHT_PX}px - ${chromePx}px)`;

/** Buckets apps into switcher sections, dropping groups that have no apps. */
export const groupEcareApps = (apps: readonly EcareApp[]): EcareAppSection[] =>
  (Object.keys(ECARE_APP_GROUP_LABELS) as EcareAppGroup[])
    .map((group) => ({
      group,
      label: ECARE_APP_GROUP_LABELS[group],
      apps: apps.filter((app) => app.group === group),
    }))
    .filter((section) => section.apps.length > 0);
